import { getAllowedSeatTypes, getWorkspacePlanType } from 'constants/workspaces'

import { SubscriptionPlan, WorkspaceSeatType } from 'models/user'

import { useMemo } from 'react'

interface UseAllowedSeatTypesResult {
  /** Resolved plan-type bucket, or `undefined` while the plan is still unknown. */
  planType: ReturnType<typeof getWorkspacePlanType>
  /** Seat types the plan permits assigning, expanded once the restricted-member feature is on. */
  allowedSeatTypes: WorkspaceSeatType[]
  /** False while the plan is unresolved — callers should block sending an explicit seat type until true. */
  isPlanKnown: boolean
}

/**
 * Seat types the given workspace plan permits assigning (ISSUE-2078). Derived once here so the invite popup, the members table and the update
 * dropdowns share a single source of truth instead of each re-deriving plan → allowed seat types.
 *
 * @param plan The workspace's subscription plan (may be undefined while the workspaces query loads).
 * @param isWorkspacePersonal Fallback for when the workspace plan is missing but the workspace is known to be personal.
 * @param granterSeatType The acting user's own seat type. A RESTRICTED-seat granter can only ever
 *   assign a Restricted seat — they hold no paid seat to grant — so the result is capped to
 *   `[RESTRICTED]` regardless of what the plan would otherwise permit (ISSUE-2488).
 */
export const useAllowedSeatTypes = (plan: SubscriptionPlan | undefined, isWorkspacePersonal = false, granterSeatType?: WorkspaceSeatType): UseAllowedSeatTypesResult => {
  const planType = getWorkspacePlanType(plan, isWorkspacePersonal)
  const allowedSeatTypes = useMemo(
    () => (granterSeatType === WorkspaceSeatType.RESTRICTED ? [WorkspaceSeatType.RESTRICTED] : getAllowedSeatTypes(planType)),
    [planType, granterSeatType]
  )

  return { planType, allowedSeatTypes, isPlanKnown: planType !== undefined }
}
